"use client";

import { CLOUDINARY_CLOUD } from "@/lib/cloudinary";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Menu } from "lucide-react";
import { isMobile } from "react-device-detect";

import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface HeroNavVideoProps {
  videoUrl?: string;
  poster?: string;
  subtitle?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  secondButtonText?: string;
  secondButtonHref?: string;
}

const HeroNavVideo = ({ 
  videoUrl,
  poster,
  subtitle = "",
  title = "",
  description = "",
  buttonText,
  secondButtonText = "Nuestras propiedades",
  secondButtonHref = "/propiedades/venta"
}: HeroNavVideoProps = {}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const backgroundVideo = videoUrl || `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/video/upload/q_auto/f_auto/v1704039442/person/website/backng2_wtfliv.mp4`;
  const posterImage = poster || "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/full-width-backgrounds/andrew-kliatskyi-MaVm_A0xhKk-unsplash.jpg";

  // Defer video loading to improve Speed Index on both mobile and desktop
  useEffect(() => {
    // Wait for page to be fully interactive before loading video
    // This improves Speed Index significantly without hurting SEO or initial paint
    // (poster image loads immediately for visual content)
    const timer = setTimeout(() => {
      setShouldLoadVideo(true);
    }, 3500); // 3.5 second delay for better Speed Index
    return () => clearTimeout(timer);
  }, []);

  const navigationLinks = [
    { href: "/propiedades/venta", label: "Propiedades" },
    { href: "/propietarios", label: "Vende" },
    { href: "/personup", label: "PersonUp" },
    { href: "/universo-person", label: "Universo person" },
    { href: "/empleo", label: "Join us" },
    { href: "/blog", label: "Magazine" },
    { href: "/contacto", label: "Contacto" },
  ];

  return (
    <section className="font-poppins dark relative min-h-svh max-h-[1400px] w-full overflow-hidden bg-gray-900">
      {/* Optimized poster image with Next.js Image */}
      <Image
        src={posterImage}
        alt="Hero background"
        fill
        priority
        quality={80}
        sizes="100vw"
        className="object-cover"
        referrerPolicy="no-referrer-when-downgrade"
      />

      {/* Background Video with deferred loading for better Speed Index */}
      {shouldLoadVideo && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
              preload="none"
          className="absolute left-0 top-0 h-full w-full object-cover z-[1]"
          crossOrigin="anonymous"
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      )}

      {/* Dark overlay */}
      <div className="absolute left-0 top-0 h-full w-full bg-black/65 z-10" />

      <div className="relative z-20 flex h-full min-h-svh max-h-[1400px] flex-col">
        {/* Navigation Header */}
        <div className="w-full flex items-center justify-between pt-4 md:pt-8 pb-4 px-4 sm:px-6 md:px-12 lg:px-16">
          {/* Left side: Logo, separator, and Navigation Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Logo */}
            <Link href="/" className="relative h-10 w-28 md:h-14 md:w-40 flex-shrink-0">
              <Image
                src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/logos/logonbflat_ybusfx.png`}
                alt="Company Logo"
                fill
                className="object-contain object-center"
                priority
              />
            </Link>

            {/* Separator - hidden on mobile */}
            <div className="h-8 w-px bg-white/30 hidden md:block" />

            {/* Navigation Menu - hidden on mobile */}
            <NavigationMenu className="hidden lg:block">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/propiedades/venta"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Propiedades
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/propietarios"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Vende
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/personup"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    PersonUp
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/universo-person"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Universo person
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/empleo"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Join us
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/blog"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Magazine
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/contacto"
                    className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    )}
                  >
                    Contacto
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right side: Mobile Menu & CTA */}
          <div className="flex items-center gap-4">
            {/* Desktop CTA - hidden on mobile */}
            <Link
              href="/valora-tu-vivienda"
              className="z-10 hidden lg:inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm text-white transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none whitespace-nowrap cursor-pointer"
            >
              Valora tu vivienda
            </Link>

            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <button
                  className="text-white hover:text-white/80 transition-colors p-2 cursor-pointer"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-[#193144] p-6 [&>button]:text-white [&>button]:hover:text-white/80">
                <SheetHeader className="pb-6">
                  <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                  <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex-shrink-0">
                    <Image
                      src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/person/white_logo_transparent_cropped_btsmlv.png`}
                      alt="Person Logo"
                      width={96}
                      height={32}
                      className="object-contain object-left h-10 w-auto"
                    />
                  </Link>
                </SheetHeader>
                <nav className="flex flex-col gap-4">
                  {navigationLinks.map((link, index) => (
                    <Link
                      key={index}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-white hover:text-white/80 transition-colors py-2 text-base"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    href="/valora-tu-vivienda"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-white hover:text-white/80 transition-colors py-2 text-base border-t border-white/20 pt-4 mt-2 cursor-pointer"
                  >
                    Valora tu vivienda
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Hero Content - grows to fill remaining space and pushes content to bottom */}
        <div className="flex-1 flex items-end justify-center pb-8 sm:pb-12 px-4 sm:px-6 md:px-12 lg:px-16">
          <div className="flex w-full max-w-[75rem] mx-auto flex-col gap-4 sm:gap-5">
            <div className="flex flex-col gap-1">
              <p className="text-muted-foreground text-xs sm:text-sm uppercase leading-none">
                {subtitle}
              </p>
              <h1 className="leading-snug! text-foreground text-2xl sm:text-3xl md:text-4xl lg:text-3xl">
                {title}
              </h1>
            </div>
            <div className="flex w-full flex-col justify-between gap-4 sm:gap-5 sm:flex-row sm:items-center">
              <p className="border-muted-foreground max-w-[20.25rem] border-l pl-4 sm:pl-6 text-base text-white leading-relaxed">
                {description}
              </p>
              <div className="shrink-0 flex flex-col sm:flex-row gap-3">
              {buttonText && (
                <Button
                  asChild
                  className="!bg-white border border-gray-200 !text-gray-700 hover:!bg-white hover:!text-gray-700 cursor-pointer px-6 sm:px-8 py-4 sm:py-6 text-xs sm:text-sm rounded-md w-full sm:w-auto"
                >
                  <a href="#">
                    {buttonText}
                  </a>
                </Button>
                )}
                {secondButtonText && (
                  <Button
                    asChild
                    className="bg-[#1A3043] hover:bg-[#0A3043] text-white cursor-pointer px-6 sm:px-8 font-normal py-4 sm:py-6 text-xs sm:text-sm rounded-md w-full sm:w-auto"
                  >
                    <a 
                      href={secondButtonHref || "#"}
                      target={secondButtonHref && !secondButtonHref.startsWith('#') ? '_blank' : undefined}
                      rel={secondButtonHref && !secondButtonHref.startsWith('#') ? 'noopener noreferrer' : undefined}
                    >
                      {secondButtonText}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { HeroNavVideo };

